import { AUTH_BASE_PATH } from "@/lib/auth/constants"
import { AUTH_TEST_MODE } from "@/lib/test-auth"

/**
 * Browser-side auth glue for the BFF flow (replaces the old src/lib/msal.ts).
 *
 * The server holds the refresh token in the encrypted Auth.js session cookie;
 * the browser only ever holds the ~1h ID token, fetched from GET /auth/token
 * and cached in memory until near expiry. api.ts attaches it as the bearer —
 * in this CIAM single-tier setup the ID token IS the API bearer
 * (docs/security-notes.md §2).
 *
 * Kept free of React imports (same indirection as before) so api.ts and
 * billing.ts can consume auth state from plain modules; the react side feeds
 * it via setActiveAuthUser (src/providers/auth-provider.tsx).
 */

/** Refresh the cached token this long before it actually expires. */
const TOKEN_SKEW_MS = 60_000

interface CachedToken {
  token: string
  /** Epoch ms at which the token expires. */
  expiresAtMs: number
}

let _cached: CachedToken | null = null
let _inflight: Promise<string | null> | null = null

/**
 * Thrown when /auth/token reports a TRANSIENT refresh failure (503) or is
 * unreachable: the session is still alive server-side, so callers must NOT
 * treat this as signed-out — surface it as a normal request failure and let
 * TanStack Query retry.
 */
export class TokenRefreshUnavailableError extends Error {
  constructor() {
    super("Sign-in service temporarily unavailable — retrying shortly.")
    this.name = "TokenRefreshUnavailableError"
  }
}

/**
 * The current API bearer (ID token), refreshed server-side when needed.
 * Resolves null when signed out or when re-authentication is required —
 * the request then goes out unauthenticated and the API's 401 routes the
 * user back to sign-in via notifyUnauthorized.
 */
export async function acquireApiToken(): Promise<string | null> {
  // E2E auth seam (test-auth.ts): dead code in production builds. Requests go
  // out without a bearer — fine, E2E runs against a mocked API.
  if (AUTH_TEST_MODE) return null
  if (_cached && _cached.expiresAtMs - TOKEN_SKEW_MS > Date.now()) {
    return _cached.token
  }
  // Serialize concurrent callers onto one /auth/token round-trip.
  _inflight ??= fetchToken().finally(() => {
    _inflight = null
  })
  return _inflight
}

async function fetchToken(): Promise<string | null> {
  let res: Response
  try {
    res = await fetch(`${AUTH_BASE_PATH}/token`, { cache: "no-store" })
  } catch {
    throw new TokenRefreshUnavailableError()
  }
  if (res.status === 401) {
    // No session, or the refresh token is permanently dead.
    _cached = null
    return null
  }
  if (!res.ok) throw new TokenRefreshUnavailableError()
  const data = (await res.json()) as { token: string; expiresAt: number }
  _cached = { token: data.token, expiresAtMs: data.expiresAt * 1000 }
  return data.token
}

/** Drop the in-memory token — call on sign-out and on a dead-session 401. */
export function clearCachedApiToken(): void {
  _cached = null
}

// ---------------------------------------------------------------------------
// Unauthorized (401) handling — api.ts stays free of React imports. AuthGate
// registers a handler that clears the dead session and drops the user back on
// the sign-in landing.
// ---------------------------------------------------------------------------

type UnauthorizedHandler = () => void

let _onUnauthorized: UnauthorizedHandler | null = null

/**
 * Registers a callback invoked when the API rejects a request with 401 — i.e.
 * the session is unrecoverable (token expired/revoked and server-side refresh
 * still produced a token the backend won't accept). Pass null on unmount.
 */
export function setUnauthorizedHandler(fn: UnauthorizedHandler | null): void {
  _onUnauthorized = fn
}

/** Called by the API client on a 401 so the auth layer can force re-authentication. */
export function notifyUnauthorized(): void {
  _onUnauthorized?.()
}

// ---------------------------------------------------------------------------
// Active user snapshot — set by the SessionBridge (auth-provider.tsx) from the
// Auth.js session so non-React modules (billing.ts) can read the identity.
// ---------------------------------------------------------------------------

export interface ActiveAuthUser {
  /** Entra object id (oid). */
  oid: string | null
  email: string | null
  name: string | null
}

let _activeUser: ActiveAuthUser = { oid: null, email: null, name: null }

/** Called by the auth provider whenever the session changes (incl. sign-out). */
export function setActiveAuthUser(user: ActiveAuthUser): void {
  _activeUser = user
}

/**
 * The signed-in user's Entra object id (oid), or null when signed out. This is
 * the identity the backend resolves via GetByEntraUserIdAsync — and therefore
 * the ONLY valid RevenueCat appUserID (#136): a purchase attached to anything
 * else never reaches this account's entitlement.
 */
export function getEntraOid(): string | null {
  return _activeUser.oid
}

/**
 * The signed-in user's email, or null when signed out. Used to prefill the
 * RevenueCat/Stripe checkout so the user doesn't retype it (#136).
 */
export function getEntraEmail(): string | null {
  return _activeUser.email
}
